import { useCallback, useEffect, useRef, useState } from 'react';
import { Paper, Typography } from '@mui/material';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { getGuardDutyConfig } from '../../../services/guarddutyApi';
import GuardDutySetup from './GuardDutySetup';
import ThreatDashboard from './ThreatDashboard';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { CK, FONT_BODY, sectionCardSx, sectionHeadingSx } from '../portalStyles';

// GuardDuty accent — the same orange the sidebar, header avatars and buttons
// use across every GuardDuty route.
const GD = '#E65100';

const entryHeader = (
  <PortalPageHeader
    icon={<GppMaybeIcon sx={{ fontSize: 24 }} />}
    title="GuardDuty"
    subtitle="Threat detection across your organization"
    color={GD}
  />
);

// GuardDutyEntry decides what to render at /securityhub/guardduty. Flow:
//   no config / disabled / failed  → setup wizard
//   in_progress                    → transitioning view (polls until done)
//   enabled / partial_failure      → threat dashboard
//
// The "in_progress" branch handles both initial enable (after the wizard
// submits) AND teardown initiated from Settings → Danger Zone, which
// navigates here so this component's polling drives the UI forward.
export default function GuardDutyEntry() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getGuardDutyConfig();
      setConfig(res?.data || null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll during any in_progress transition (enable or disable) so the view
  // advances automatically once the orchestrator goroutine finishes.
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  // Loading and error keep the page header mounted and swap only the card body,
  // so the layout does not jump once the config lands.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{entryHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading GuardDuty…" color={GD} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{entryHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const hint = (config.errorMessage || '').toLowerCase();
    let title = 'Enabling GuardDuty across accounts and regions…';
    let body = 'Enrolling member accounts, enabling protection plans, and configuring the delegated administrator. This can take a few minutes.';
    if (hint.includes('disabl')) {
      title = 'Disabling GuardDuty…';
      body = 'Tearing down members and detectors across every linked region. This can take a few minutes.';
    } else if (hint.includes('updating features') || hint.includes('features')) {
      title = 'Updating protection plans…';
      body = 'Enabling / disabling the requested GuardDuty features across every member account and region.';
    }
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{entryHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>{title}</Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "not usably configured" states — surface the setup wizard so the
  // user can re-run. EnableGuardDuty clears any prior failed/disabled row
  // server-side before creating a new one.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <GuardDutySetup
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  return <ThreatDashboard config={config} onConfigChange={refresh} />;
}
